import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useStableHandlers } from "@/hooks/ui/useStableHandlers";
import { useQueryClient } from "@tanstack/react-query";
import { useGetAuthorizationUrl } from "@/hooks/auth/useSalesforceAuthGraphQL";
import {
	isFreshActiveRun,
	useAbortRun,
	useExecutePlan,
	useHealRun,
	useOpenUrl,
	usePlan,
	usePlans,
	useRuns,
	useUpdatePlan,
	useVerifyPlan,
} from "@/hooks/testing";
import { useTestingChat } from "@/hooks/testing/useTestingChat";
import { useChatThreadHydration } from "@/hooks/testing/useChatThreadHydration";
import {
	type DestructiveAction,
	type ExecuteResult,
	type ExecuteStreamEvent,
	isExecutablePlan,
	isTestingApiError,
	type TestPlan,
	type TestRun,
} from "@/services/testing";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { readPersistedTestingSelection, useTestingStore } from "@/stores/testingStore";
import { useActiveOrgId } from "@/lib/org-scope/react";
import { isCredentialError } from "@/utils/testing/planDisplay";
import { planExecutionPreview } from "@/utils/testing/planPreview";
import { resolvePrimaryAction } from "@/utils/testing/primaryAction";
import {
	activeSessionOf,
	buildSessionList,
	buildSessions,
	owningScopeForPlan,
	type TestingSession,
} from "@/utils/testing/sessions";
import {
	lastPlanMessageIndexByPlanId,
	runsForPlan,
} from "@/components/organisms/testing/chatTimeline";

const TESTING_CHAT_DRAFT_KEY = "ncoder.testing.chatDraft";

/**
 * Where one chat draft lives.
 *
 * Namespaced by organization as well as user and scope. Without the org, a draft
 * typed into Org A's new-plan composer (`activeChatScope` is scope-local, not
 * org-local) was restored verbatim into Org B's — the same class of leak as the
 * stores, in six characters of string concatenation.
 */
function testingChatDraftKey(userId: string, orgId: string | null, scope: string): string {
	return `${TESTING_CHAT_DRAFT_KEY}:${orgId ?? "no-org"}:${userId}:${scope}`;
}

/**
 * Triage categories a bounded IR patch can plausibly address. Module scope so the Set is built once
 * rather than on every render of the hook.
 */
const SELF_HEALABLE = new Set([
	"SELECTOR_DRIFT",
	"VIEW_RENDER_GAP",
	"TIMING_ASYNC",
	"PAGE_LAYOUT_MISMATCH",
]);

export type ClarificationData = {
	run_id: string;
	failing_label: string;
	suggestion: string;
	type: "clarification";
};

function runToExecuteResult(run: TestRun): ExecuteResult | null {
	const raw = (run.summary ?? {}) as Record<string, unknown>;
	if (!raw || Object.keys(raw).length === 0) return null;
	const nested =
		raw.summary && typeof raw.summary === "object" && !Array.isArray(raw.summary)
			? (raw.summary as Record<string, unknown>)
			: raw;
	// `status` on a persisted run is only ever passed|failed|error, so an amber run (blocked /
	// degraded / passed_with_retry) reads as a flat red failure unless `execution_state` is carried
	// across too. Lift it to the top level as well as leaving it on `summary`.
	const executionState =
		typeof nested.execution_state === "string"
			? (nested.execution_state as ExecuteResult["execution_state"])
			: typeof raw.execution_state === "string"
				? (raw.execution_state as ExecuteResult["execution_state"])
				: undefined;
	return {
		run_id: run.id,
		// status stays passed|failed here on purpose — the amber detail rides on execution_state.
		status: run.status === "passed" ? "passed" : "failed",
		execution_state: executionState,
		duration_ms: typeof raw.duration_ms === "number" ? raw.duration_ms : 0,
		step_results: Array.isArray(raw.step_results)
			? (raw.step_results as ExecuteResult["step_results"])
			: [],
		summary: nested as ExecuteResult["summary"],
	};
}

export function useTestingLayout() {
	const uid = useAuthStoreV1((s) => s.user?.id ?? "anon");
	const ensureUser = useTestingStore((s) => s.ensureUser);
	useEffect(() => { ensureUser(uid); }, [uid, ensureUser]);

	const selectedPlanId = useTestingStore((s) => s.selectedPlanId);
	const setSelectedPlanId = useTestingStore((s) => s.setSelectedPlanId);
	const editingPlanId = useTestingStore((s) => s.editingPlanId);
	const setEditingPlanId = useTestingStore((s) => s.setEditingPlanId);
	const activeChatScope = useTestingStore((s) => s.activeChatScope);
	const setActiveChatScope = useTestingStore((s) => s.setActiveChatScope);
	useEffect(() => {
		if (!activeChatScope) setActiveChatScope(`new:${Date.now()}`);
	}, [activeChatScope, setActiveChatScope]);

	const chat = useTestingChat(activeChatScope);
	const [draft, setDraft] = useState(() => {
		if (typeof window === "undefined") return "";
		const authState = useAuthStoreV1.getState();
		const uid = authState.user?.id ?? "anon";
		const key = testingChatDraftKey(uid, authState.selectedOrg?.id ?? null, activeChatScope);
		return window.sessionStorage.getItem(key) ?? "";
	});

	const historyPlanId = useTestingStore((s) => s.historyPlanId);
	const setHistoryPlanId = useTestingStore((s) => s.setHistoryPlanId);
	const liveExpanded = useTestingStore((s) => s.liveExpanded);
	const setLiveExpanded = useTestingStore((s) => s.setLiveExpanded);
	const completedRuns = useTestingStore((s) => s.completedRuns);
	const setCompletedRuns = useTestingStore((s) => s.setCompletedRuns);
	const invalidateRunsForPlan = useTestingStore((s) => s.invalidateRunsForPlan);

	const wasStreaming = useRef(false);
	// One-turn auto-edit flag: set when a repair prompt auto-enters edit mode.
	// Cleared the moment the user makes any explicit intent choice.
	const repairAutoEditRef = useRef(false);

	const queryClient = useQueryClient();
	const [showCredentialError, setShowCredentialError] = useState(false);
	// Destructive-run confirmation gate: set only while a run/verify awaits the
	// user's explicit OK because the plan deletes records or edits an existing one.
	// `mode` remembers which action to resume on "Run anyway" (Run, or Verify — verify runs the plan
	// headless, so it is destructive too).
	const [pendingDestructiveRun, setPendingDestructiveRun] = useState<{
		planId: string;
		actions: DestructiveAction[];
		mode: "run" | "verify";
	} | null>(null);
	// Optimistic stop state: true from the instant the user clicks Stop until the
	// run has actually wound down, so the UI reflects intent ("Stopping…") instead
	// of looking like nothing happened while the backend winds the run down.
	const [stopping, setStopping] = useState(false);
	// A Stop clicked before `run_started` delivers a run id: remember it and fire
	// the abort the moment the id is known, so Stop is never a silent no-op.
	const stopRequestedRef = useRef(false);
	const getAuthUrlMutation = useGetAuthorizationUrl();

	const authReady = useAuthStoreV1((s) => !!(s.token && s.user?.id && s.selectedOrg?.id));
	const userId = useAuthStoreV1.getState().user?.id ?? "anon";

	// ─── Data fetching ────────────────────────────────────────────────────────
	const plansQuery = usePlans();
	const selectedPlanQuery = usePlan(selectedPlanId);
	const historyTargetPlanId = historyPlanId ?? selectedPlanId;
	const historyQuery = useRuns(historyTargetPlanId);
	const { data: plans = [] } = plansQuery;
	const { data: selectedPlan } = selectedPlanQuery;

	// Rebuild the chat threads of persisted conversations on load, so a reload restores the full thread
	// (cards + in-chat scroll), not just the grouping. Additive — never clobbers a live thread.
	useChatThreadHydration(plans, authReady);
	const { data: history = [] } = historyQuery;
	const { data: openSfUrl = null } = useOpenUrl(selectedPlanId);

	// Restore the persisted plan selection on a hard reload (F5 / full navigation):
	// re-selecting the plan re-enables the runs poll, so an in-progress run
	// RECONNECTS instead of vanishing. Mirrors multiAgentStore's persistence.
	// Once only, and only if nothing is selected yet and the plan still exists —
	// so it never fights a deliberate deselect or the user's own selection.
	const restoredSelectionRef = useRef(false);
	useEffect(() => {
		if (restoredSelectionRef.current || selectedPlanId || plans.length === 0) return;
		restoredSelectionRef.current = true;
		const persisted = readPersistedTestingSelection().selectedPlanId;
		if (persisted && plans.some((p) => p.id === persisted)) setSelectedPlanId(persisted);
	}, [plans, selectedPlanId, setSelectedPlanId]);

	// ─── Mutations + streaming hooks ──────────────────────────────────────────
	const updatePlanMutation = useUpdatePlan();
	const executePlan = useExecutePlan();
	const abortRunMutation = useAbortRun();
	const healRun = useHealRun();
	const verifyPlan = useVerifyPlan();

	// ─── Derived run state ────────────────────────────────────────────────────
	const running = executePlan.isStreaming;
	const planningActive = chat.streaming;
	const healing = healRun.isStreaming;
	// A heal produces a PROPOSAL, never a run verdict — the source run keeps the result it got.
	// (This used to read `healRun.result ?? executePlan.result`, which let a heal overwrite the
	// verdict of the run it was diagnosing.)
	const runResult = executePlan.result ?? null;

	const streamedRunId =
		[...executePlan.events, ...healRun.events]
			.reverse()
			.find((e): e is { type: "run_started"; run_id: string } => e.type === "run_started")
			?.run_id ?? null;

	const serverActiveRun = history.find(isFreshActiveRun) ?? null;
	const activeRunId = runResult?.run_id ?? streamedRunId ?? serverActiveRun?.id ?? null;
	const runError = executePlan.error?.message ?? healRun.error?.message ?? null;

	useEffect(() => {
		if (runError && isCredentialError(runError)) setShowCredentialError(true);
	}, [runError]);

	// Auto-enter edit mode for a failed run that needs repair — one-turn only.
	useEffect(() => {
		if (runResult?.status !== "failed" || !runResult.run_id) return;
		const q = runResult.summary.question;
		if (!q || q.requiresUserAnswer === false) return;
		if (selectedPlanId) {
			setEditingPlanId(selectedPlanId);
			repairAutoEditRef.current = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [runResult?.run_id, runResult?.status]);

	/** Every event of the run that is streaming right now, regardless of which session owns it. */
	const activeRunEvents: ExecuteStreamEvent[] = running
		? executePlan.events
		: healing
			? healRun.events
			: [];
	const hasFreshServerRun = serverActiveRun !== null;

	// Reconnect feed: map persisted live_events back to the flat event shape.
	const reconnectEvents = useMemo<ExecuteStreamEvent[]>(() => {
		const le = (serverActiveRun?.summary as { live_events?: Array<{ type: string; data?: Record<string, unknown> }> } | null | undefined)?.live_events;
		if (!Array.isArray(le)) return [];
		return le.map((e) => ({ type: e.type, ...(e.data ?? {}) })) as ExecuteStreamEvent[];
	}, [serverActiveRun]);

	const planAnchors = useMemo(() => lastPlanMessageIndexByPlanId(chat.messages), [chat.messages]);

	// Start time for the live elapsed clock.
	const [localRunStartAt, setLocalRunStartAt] = useState<number | null>(null);
	useEffect(() => {
		if ((running || healing) && localRunStartAt == null) setLocalRunStartAt(Date.now());
		else if (!running && !healing && localRunStartAt != null) setLocalRunStartAt(null);
	}, [running, healing, localRunStartAt]);
	const serverRunStartMs = serverActiveRun?.started_at ? Date.parse(String(serverActiveRun.started_at)) : Number.NaN;
	const runStartedAtMs = localRunStartAt ?? (Number.isFinite(serverRunStartMs) ? serverRunStartMs : null);

	const clarificationEvent: ClarificationData | undefined = activeRunEvents
		.filter((e): e is ExecuteStreamEvent & { type: "runtime_activity"; kind: "question"; data: ClarificationData } =>
			e.type === "runtime_activity" &&
			(e as { kind?: string }).kind === "question" &&
			typeof (e as { data?: unknown }).data === "object" &&
			(e as { data?: { type?: unknown } }).data?.type === "clarification",
		)
		.map((e) => (e as { data: ClarificationData }).data)
		.at(0);

	const { reset: resetExecute } = executePlan;
	const { reset: resetHeal } = healRun;

	// Clear live-run state on session switch so plan A's result never bleeds into plan B.
	const prevScopeRef = useRef<string | undefined>(undefined);
	useEffect(() => {
		if (prevScopeRef.current === undefined) { prevScopeRef.current = activeChatScope; return; }
		if (prevScopeRef.current === activeChatScope) return;
		prevScopeRef.current = activeChatScope;
		resetExecute();
		resetHeal();
	}, [activeChatScope, resetExecute, resetHeal]);

	// Re-point history footer to the active session's plan on session switch.
	const prevSelForHistoryRef = useRef<string | null | undefined>(undefined);
	useEffect(() => {
		if (prevSelForHistoryRef.current === undefined) { prevSelForHistoryRef.current = selectedPlanId; return; }
		if (prevSelForHistoryRef.current === selectedPlanId) return;
		prevSelForHistoryRef.current = selectedPlanId;
		setHistoryPlanId(selectedPlanId);
	}, [selectedPlanId, setHistoryPlanId]);

	// Capture run's owning plan at LEADING EDGE so attribution is correct even if user re-selects mid-run.
	const runActiveRef = useRef(false);
	const [liveRunPlanId, setLiveRunPlanId] = useState<string | null>(null);
	useEffect(() => {
		const active = running || healing;
		if (active && !runActiveRef.current) setLiveRunPlanId(selectedPlanId);
		runActiveRef.current = active;
	}, [running, healing, selectedPlanId]);

	/**
	 * Whether the live run belongs to the session the user is LOOKING AT.
	 *
	 * The distinction is what makes the module navigable mid-run. `running`/`healing` are global (one
	 * browser job at a time, so they gate starting a second run), while every piece of run UI — the
	 * step feed, the live browser, the elapsed clock, the Stop button — is keyed off `runningHere` so
	 * it appears on the owning session and only there. Browsing to another session no longer tears the
	 * stream down; it simply stops rendering it, and coming back shows it again.
	 */
	const runIsForSelected = liveRunPlanId != null && liveRunPlanId === selectedPlanId;
	const runningHere = running && runIsForSelected;
	const healingHere = healing && runIsForSelected;
	const runningElsewhere = (running || healing) && !runIsForSelected;
	/** Events scoped to the viewed session — what every panel should render. */
	const liveEvents: ExecuteStreamEvent[] = runIsForSelected ? activeRunEvents : [];

	/**
	 * Whether the LAST result of the session on screen is one a fix proposal could help with.
	 *
	 * Scoped to `runIsForSelected` deliberately: `runResult` is the module-level stream's result, so
	 * without the guard a failure in session A would offer "Propose a fix" on session B's header — and
	 * healing it would diagnose A's run while the user was looking at B.
	 */
	const canSelfHealRun =
		runIsForSelected &&
		runResult?.status === "failed" &&
		(runResult.summary.triage?.retryRecommended === true ||
			(!!runResult.summary.triage?.category && SELF_HEALABLE.has(runResult.summary.triage.category)));

	const selectedResult =
		!!runResult && liveRunPlanId === selectedPlanId
			? runResult
			: ([...completedRuns].reverse().find((r) => r.planId === selectedPlanId)?.result ?? null);

	// Scoped to the viewed session: a run streaming for a DIFFERENT session must not suppress this
	// session's result card, otherwise browsing away from a run blanks out the test you navigated to.
	const showResultMascot =
		!!selectedResult && !runningHere && !healingHere && !planningActive && !hasFreshServerRun;

	// Push completed runs into the store so all result cards stay visible.
	useEffect(() => {
		const planId = liveRunPlanId ?? selectedPlanId;
		if (runResult && !running && !healing && planId) {
			setCompletedRuns((prev) => {
				if (prev.some((r) => r.result.run_id === runResult.run_id)) return prev;
				return [...prev, { result: runResult, planId }];
			});
		}
	}, [runResult, running, healing, selectedPlanId, liveRunPlanId]);

	// Reconnect-finish: adopt a run that completed server-side with no live stream.
	const reconnectRunIdRef = useRef<string | null>(null);
	useEffect(() => {
		const reconnecting = hasFreshServerRun && !running && !healing && liveEvents.length === 0 && !runResult;
		if (reconnecting && serverActiveRun) reconnectRunIdRef.current = serverActiveRun.id;
	}, [hasFreshServerRun, running, healing, liveEvents.length, runResult, serverActiveRun]);
	useEffect(() => {
		const rid = reconnectRunIdRef.current;
		if (!rid || !selectedPlanId) return;
		const finished = history.find(
			(r) => r.id === rid && (r.status === "passed" || r.status === "failed" || r.status === "error" || r.status === "aborted"),
		);
		if (!finished) return;
		const adapted = runToExecuteResult(finished);
		if (!adapted) return;
		reconnectRunIdRef.current = null;
		setCompletedRuns((prev) => {
			if (prev.some((r) => r.result.run_id === rid)) return prev;
			return [...prev, { result: adapted, planId: selectedPlanId }];
		});
	}, [history, selectedPlanId]);

	// Refresh plan cache after streaming ends (in-place edits change name/status).
	useEffect(() => {
		if (wasStreaming.current && !chat.streaming && selectedPlanId) {
			void queryClient.invalidateQueries({ queryKey: ["testingPlan", selectedPlanId] });
			void queryClient.invalidateQueries({ queryKey: ["testingPlans"] });
		}
		wasStreaming.current = chat.streaming;
	}, [chat.streaming, selectedPlanId, queryClient]);

	// Promote a draft scope to the plan's own session on first proposal.
	useEffect(() => {
		if (!chat.lastProposedPlanId) return;
		const target = `plan:${chat.lastProposedPlanId}`;
		if (activeChatScope.startsWith("new:") && activeChatScope !== target && !useTestingStore.getState().byScope[target]) {
			chat.moveScope(activeChatScope, target);
			setActiveChatScope(target);
		}
		setSelectedPlanId(chat.lastProposedPlanId);
		void queryClient.invalidateQueries({ queryKey: ["testingPlans"] });
		// activeChatScope intentionally omitted — including it would re-fire after setActiveChatScope.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chat.lastProposedPlanId, chat.moveScope, queryClient]);

	// Clear stale selectedPlanId if the plan was deleted.
	useEffect(() => {
		if (plansQuery.isFetching) return;
		if (selectedPlanId && selectedPlanId === chat.lastProposedPlanId) return;
		if (selectedPlanId && plans.length > 0 && !plans.find((p) => p.id === selectedPlanId)) setSelectedPlanId(null);
	}, [plans, selectedPlanId, plansQuery.isFetching, chat.lastProposedPlanId]);

	useEffect(() => {
		if (plansQuery.isFetching) return;
		if (editingPlanId && editingPlanId === chat.lastProposedPlanId) return;
		if (editingPlanId && plans.length > 0 && !plans.find((p) => p.id === editingPlanId)) setEditingPlanId(null);
	}, [plans, editingPlanId, plansQuery.isFetching, chat.lastProposedPlanId, setEditingPlanId]);

	// Per-session draft persistence, scoped to the organization the draft is about.
	const draftOrgId = useActiveOrgId();
	const draftScopeRef = useRef(activeChatScope);
	useEffect(() => {
		if (draftScopeRef.current !== activeChatScope) return;
		window.sessionStorage.setItem(testingChatDraftKey(userId, draftOrgId, activeChatScope), draft);
	}, [draft, activeChatScope, userId, draftOrgId]);
	useEffect(() => {
		if (draftScopeRef.current === activeChatScope) return;
		draftScopeRef.current = activeChatScope;
		setDraft(
			window.sessionStorage.getItem(testingChatDraftKey(userId, draftOrgId, activeChatScope)) ?? "",
		);
	}, [activeChatScope, userId, draftOrgId]);

	// ─── Handlers ─────────────────────────────────────────────────────────────

	// A request to scroll the chat to a plan's card. Selecting a plan from the left list sets this; the
	// chat panel watches the nonce and scrolls. Kept SEPARATE from selection so a newly PROPOSED plan
	// (which should ride the normal follow-the-conversation autoscroll to the bottom) does not trigger a
	// jump, while a click on an older plan in the list does. The nonce lets re-selecting the same plan
	// scroll again.
	const [planScrollRequest, setPlanScrollRequest] = useState<{ planId: string; nonce: number } | null>(
		null,
	);
	const scrollNonceRef = useRef(0);
	function requestPlanScroll(planId: string) {
		scrollNonceRef.current += 1;
		setPlanScrollRequest({ planId, nonce: scrollNonceRef.current });
	}

	function onChatSubmit(e: FormEvent) {
		e.preventDefault();
		const text = draft.trim();
		if (!text || !authReady || running || healing) return;
		chat.send(text, editingPlanId);
		if (repairAutoEditRef.current) {
			repairAutoEditRef.current = false;
			setEditingPlanId(null);
		}
		setDraft("");
		window.sessionStorage.removeItem(`${TESTING_CHAT_DRAFT_KEY}:${userId}:${activeChatScope}`);
	}

	function focusChatInput() {
		window.setTimeout(() => {
			const el =
				document.querySelector<HTMLTextAreaElement>('form textarea[placeholder^="Tweak"]') ||
				document.querySelector<HTMLTextAreaElement>('form textarea[placeholder^="Describe"]');
			el?.focus();
		}, 0);
	}

	/**
	 * Discard local run/heal stream state — ONLY safe when nothing is streaming.
	 *
	 * Navigation used to call resetExecute()/resetHeal() unconditionally, which aborted the live SSE
	 * reader. That is why every navigation handler had to refuse while a run was in flight, freezing
	 * the user on one screen for the several minutes a browser run takes. Now the stream is left alone
	 * whenever it is active; `runningHere` decides whether it is rendered.
	 */
	function clearIdleRunState() {
		if (running || healing) return;
		resetExecute();
		resetHeal();
	}

	/**
	 * Open a session. Allowed at ANY time, including mid-run: the run keeps streaming (it lives in the
	 * module-level store, and the server owns it regardless), and the session that owns it keeps its
	 * live dot in the list so the user can always get back to it.
	 */
	/**
	 * Select a plan and bind the chat to a given scope. Re-selecting the same plan+scope just scrolls
	 * back to its card (bumped before the early return, so a no-op selection still recentres the view).
	 */
	function openPlanInScope(planId: string, scope: string) {
		requestPlanScroll(planId);
		if (selectedPlanId === planId && activeChatScope === scope) return;
		repairAutoEditRef.current = false;
		setEditingPlanId(null);
		clearIdleRunState();
		setSelectedPlanId(planId);
		setActiveChatScope(scope);
	}

	function selectPlan(id: string) {
		openPlanInScope(id, owningScopeForPlan(id, useTestingStore.getState().byScope));
	}

	/** Re-open an in-store chat thread that has not produced a plan yet (a draft session). */
	function selectDraftScope(scopeKey: string) {
		if (activeChatScope === scopeKey) return;
		repairAutoEditRef.current = false;
		setEditingPlanId(null);
		clearIdleRunState();
		setSelectedPlanId(null);
		setActiveChatScope(scopeKey);
	}

	/** One entry point for the session list, whichever kind of session was clicked. */
	function selectSession(session: TestingSession) {
		if (session.planId) selectPlan(session.planId);
		else selectDraftScope(session.scopeKey);
	}

	/**
	 * Open a CONVERSATION from the History pane. Selecting its most-recent plan opens the shared thread
	 * (they all live in one scope) and lands on the latest work; a draft with no plan just re-opens its
	 * thread. The left "plans in this session" pane then lists the rest.
	 */
	function selectSessionGroup(group: { scopeKey: string; plans: TestingSession[] }) {
		const latest = group.plans.at(-1);
		// Open the conversation's canonical scope (group.scopeKey === plan:<firstPlanId>) directly, not
		// via selectPlan's byScope lookup: on a reload that scope is still hydrating, so deriving it from
		// byScope would land on a transient plan:<latestId> the thread never populates.
		if (latest?.planId) openPlanInScope(latest.planId, group.scopeKey);
		else selectDraftScope(group.scopeKey);
	}

	function newPlanChat() {
		repairAutoEditRef.current = false;
		setEditingPlanId(null);
		clearIdleRunState();
		const key = `new:${Date.now()}`;
		setSelectedPlanId(null);
		setActiveChatScope(key);
		setDraft("");
		window.sessionStorage.removeItem(`${TESTING_CHAT_DRAFT_KEY}:${userId}:${key}`);
	}

	function editPlanInChat(plan: TestPlan) {
		// Editing the plan that is mid-run would race the run's own spec, so that one case still waits.
		if (planningActive || (running && liveRunPlanId === plan.id) || (healing && liveRunPlanId === plan.id)) return;
		repairAutoEditRef.current = false;
		setEditingPlanId(plan.id);
		setSelectedPlanId(plan.id);
		focusChatInput();
	}

	function cancelEditMode() {
		repairAutoEditRef.current = false;
		setEditingPlanId(null);
	}

	function applyRecommendationInChat(text: string) {
		if (planningActive || runningHere || healingHere) return;
		repairAutoEditRef.current = false;
		if (selectedPlan) setEditingPlanId(selectedPlan.id);
		setDraft(text.trim());
		focusChatInput();
	}

	function startNewTestFromSuggestion(text: string) {
		if (planningActive) return;
		repairAutoEditRef.current = false;
		setEditingPlanId(null);
		setDraft(text.trim());
		focusChatInput();
	}

	/**
	 * Resolve a plan by id: prefer the live record (usePlans / usePlan) so a change made elsewhere is
	 * reflected, and fall back to the proposed-plan snapshot on the chat message (the record may not
	 * have refetched yet immediately after a proposal).
	 *
	 * `/execute` validates the stored execution contract itself — IR body, recordStrategy, UI
	 * preflight, blockers — and answers 409 with its own instructions when the plan is not runnable.
	 * The UI surfaces that message read-only; there is no approval step to offer.
	 */
	function resolvePlanSnapshot(id: string): TestPlan | null {
		const live = plans.find((p) => p.id === id) ?? (selectedPlan?.id === id ? selectedPlan : null);
		if (live) return live;
		for (let i = chat.messages.length - 1; i >= 0; i--) {
			const p = chat.messages[i].proposedPlan;
			if (p?.id === id) return p;
		}
		return null;
	}

	/** Destructive steps (delete / edit-existing) the plan will perform, if any. */
	function destructiveActionsFor(id: string): DestructiveAction[] {
		return resolvePlanSnapshot(id)?.destructive_actions ?? [];
	}

	function runPlanById(id: string, confirmed = false) {
		if (!id || running || planningActive) return;
		// Gate the RUN (never proposing/viewing) when the plan is destructive.
		if (!confirmed) {
			const actions = destructiveActionsFor(id);
			if (actions.length > 0) {
				setPendingDestructiveRun({ planId: id, actions, mode: "run" });
				return;
			}
		}
		setStopping(false);
		stopRequestedRef.current = false;
		setSelectedPlanId(id);
		setHistoryPlanId(id);
		resetExecute();
		resetHeal();
		executePlan.execute(id, {});
	}

	/**
	 * Pre-flight VERIFY runs the plan headless against the live org — so a
	 * destructive plan would delete/edit real records with no confirmation. Gate it
	 * through the SAME dialog as Run.
	 */
	function verifyPlanById(id: string, confirmed = false) {
		if (!id || verifyPlan.isPending) return;
		if (!confirmed) {
			const actions = destructiveActionsFor(id);
			if (actions.length > 0) {
				setPendingDestructiveRun({ planId: id, actions, mode: "verify" });
				return;
			}
		}
		verifyPlan.mutate({ planId: id, attempts: 3 });
	}

	/** User accepted the destructive-run warning → proceed with the gated action. */
	function confirmDestructiveRun() {
		const pending = pendingDestructiveRun;
		setPendingDestructiveRun(null);
		if (!pending) return;
		if (pending.mode === "verify") verifyPlanById(pending.planId, true);
		else runPlanById(pending.planId, true);
	}
	function cancelDestructiveRun() {
		setPendingDestructiveRun(null);
	}

	/** Best-effort server abort + local stream teardown for a known run id. */
	async function abortActiveRun(runId: string) {
		executePlan.abort();
		healRun.abort();
		try {
			await abortRunMutation.mutateAsync(runId);
		} catch {
			/* ignore — the run may already have ended */
		}
	}

	async function onStop() {
		// Nothing to stop — don't flip to "Stopping…" for a no-op.
		if (!running && !healing && !executePlan.isStreaming && !hasFreshServerRun) return;
		setStopping(true); // optimistic — reflect intent immediately
		if (activeRunId) {
			stopRequestedRef.current = false;
			await abortActiveRun(activeRunId);
			return;
		}
		// No run id yet (Stop pressed before `run_started`). Queue it and keep the
		// stream open so the id can arrive — the effect below fires the abort then.
		stopRequestedRef.current = true;
	}

	// Fire a QUEUED abort the moment a run id becomes known, so a Stop pressed
	// before `run_started` is never silently dropped.
	useEffect(() => {
		if (!stopRequestedRef.current || !activeRunId) return;
		stopRequestedRef.current = false;
		void abortActiveRun(activeRunId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeRunId]);

	// Clear the optimistic "Stopping…" state once the run has actually wound down:
	// a terminal result arrived, or nothing is executing locally or on the server.
	useEffect(() => {
		if (!stopping) return;
		if (runResult || (!running && !healing && !hasFreshServerRun && activeRunEvents.length === 0)) {
			setStopping(false);
		}
	}, [stopping, runResult, running, healing, hasFreshServerRun, activeRunEvents.length]);

	async function handleRename(id: string, name: string) {
		const trimmed = name.trim();
		if (!trimmed) return;
		await updatePlanMutation.mutateAsync({ planId: id, patch: { name: trimmed } });
	}

	function onHeal() {
		if (!activeRunId || healing || running || planningActive) return;
		resetExecute();
		healRun.heal(activeRunId);
	}

	// ─── Derived display values ───────────────────────────────────────────────

	const planStatus = selectedPlan?.status ?? null;
	/**
	 * Whether this plan is CAPABLE of running — a property of the plan, not of the moment. It no longer
	 * folds in `!running`: concurrency is `primaryAction`'s job, and mixing the two made the idle
	 * browser card tell the user to "approve the plan" whenever any other session happened to be
	 * running, even for a plan that was already approved.
	 */
	const canRun =
		selectedPlan &&
		planStatus !== "rejected" &&
		planStatus !== null &&
		isExecutablePlan(selectedPlan.steps as never);

	// A 409 from the execute open is /execute's contract validation talking. Its message is the
	// server's own instruction, so render it verbatim rather than a generic failure. Read-only: there
	// is no approval step to offer, so the fix is a re-plan in chat.
	const execApiError = executePlan.error;
	const runBlockedReason =
		execApiError && isTestingApiError(execApiError) && execApiError.status === 409
			? execApiError.message
			: null;
	// 410 on the heal open means the feature is switched off server-side — hide the affordance
	// rather than showing a broken button.
	const healDisabled = healRun.status === "disabled";

	const selectedPlanPreview = selectedPlan ? planExecutionPreview(selectedPlan) : null;

	const editingPlanName = editingPlanId
		? (plans.find((p) => p.id === editingPlanId)?.name ?? (selectedPlan?.id === editingPlanId ? selectedPlan.name : null))
		: null;

	// ─── Session list ─────────────────────────────────────────────────────────
	// One recency-ordered list covering BOTH server plans and in-store draft threads. Drafts used to
	// exist only in `byScope` with no row anywhere, so a chat that had not yet produced a plan became
	// unreachable the moment the user clicked away.
	const byScope = useTestingStore((s) => s.byScope);
	/**
	 * The session list is rebuilt only when something it actually shows changes.
	 *
	 * `byScope` is replaced on every streamed activity event — activity lives inside it — but the list
	 * reads only message counts, proposal ids and the first user message. Keying the memo on `byScope`
	 * therefore rebuilt the array (and re-rendered all five rows) ~30 times during a single planning
	 * turn, measured at 21ms of the 88ms spent on such a turn. This signature ignores activity, so the
	 * array keeps its identity and the memoized list skips those renders entirely.
	 */
	const scopeSignature = useMemo(
		() =>
			Object.keys(byScope)
				.sort()
				.map((key) => {
					const st = byScope[key];
					const proposals = st.messages
						.filter((m) => m.proposedPlan)
						.map((m) => m.proposedPlan!.id)
						.join("+");
					const firstUser = st.messages.find((m) => m.role === "user");
					return `${key}:${st.messages.length}:${st.lastProposedPlanId ?? ""}:${proposals}:${firstUser?.id ?? ""}`;
				})
				.join("|"),
		[byScope],
	);
	// Read through a ref so `byScope`'s identity is not a dependency; `scopeSignature` already covers
	// every field buildSessionList reads.
	const byScopeRef = useRef(byScope);
	byScopeRef.current = byScope;
	const sessions = useMemo(
		() =>
			buildSessionList({
				plans,
				byScope: byScopeRef.current,
				liveRunPlanId,
				runActive: running || healing,
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[plans, scopeSignature, liveRunPlanId, running, healing],
	);
	/** Which row the list highlights: the selected plan, or the draft thread being viewed. */
	const activeSessionId = selectedPlanId ?? activeChatScope;

	// ─── Conversations (sessions) vs plans ──────────────────────────────────────
	// The flat list above is one row per PLAN. Grouped by chat scope it becomes the CONVERSATIONS the
	// user actually thinks in: the right-hand History lists these (one per conversation, N plans each),
	// while the left pane shows only the plans of the conversation that is open — its table of contents.
	const sessionGroups = useMemo(() => buildSessions(sessions), [sessions]);
	const activeSession = useMemo(
		() => activeSessionOf(sessionGroups, activeChatScope, selectedPlanId),
		[sessionGroups, activeChatScope, selectedPlanId],
	);
	const activeSessionPlans = activeSession?.plans ?? [];

	// ─── The single primary action ────────────────────────────────────────────
	const primaryAction = useMemo(
		() =>
			resolvePrimaryAction({
				hasPlan: Boolean(selectedPlan),
				planExecutable: Boolean(selectedPlan && isExecutablePlan(selectedPlan.steps as never)),
				planRejected: planStatus === "rejected",
				runningHere: runningHere || healingHere,
				runningElsewhere,
				planningActive,
				canStop: Boolean(activeRunId && (runningHere || healingHere || hasFreshServerRun)),
				stopping,
			}),
		[
			selectedPlan,
			planStatus,
			runningHere,
			healingHere,
			runningElsewhere,
			planningActive,
			activeRunId,
			hasFreshServerRun,
			stopping,
		],
	);

	/**
	 * Whether the composer accepts input. Chatting is blocked only while THIS session is executing
	 * (its plan is mid-flight) or its planner is streaming — a run owned by another session leaves the
	 * user free to keep planning here, which is the whole point of a navigable session list.
	 */
	const canChat = authReady && !planningActive && !runningHere && !healingHere;

	/**
	 * Handlers are declared above as plain functions, so each is a new reference every render. Handed
	 * out raw they defeated every `memo()` in the chat — one streamed activity event re-rendered every
	 * turn, plan card and result card in the thread. Stable identities, same behaviour.
	 */
	const handlers = useStableHandlers({
		onChatSubmit,
		cancelEditMode,
		selectPlan,
		selectDraftScope,
		selectSession,
		selectSessionGroup,
		newPlanChat,
		editPlanInChat,
		applyRecommendationInChat,
		startNewTestFromSuggestion,
		runPlanById,
		verifyPlanById,
		confirmDestructiveRun,
		cancelDestructiveRun,
		onStop,
		handleRename,
		onHeal,
	});

	return {
		// Auth
		authReady,
		// Query data + queries
		plans,
		selectedPlan,
		history,
		openSfUrl,
		plansQuery,
		historyQuery,
		// Chat
		chat,
		draft,
		setDraft,
		activeChatScope,
		// Plan selection
		selectedPlanId,
		editingPlanId,
		editingPlanName,
		setEditingPlanId,
		// Sessions (plans + in-store drafts, recency ordered)
		sessions,
		activeSessionId,
		// Conversations (grouped by chat scope) + the plans of the open one.
		sessionGroups,
		activeSession,
		activeSessionPlans,
		// Scroll-to-plan request (nonce-bumped by selecting a plan in the list).
		planScrollRequest,
		// Run state. `running`/`healing` are GLOBAL (one browser job at a time); the `*Here` pair is
		// scoped to the session on screen and is what run UI should render against.
		running,
		healing,
		runningHere,
		healingHere,
		runningElsewhere,
		planningActive,
		runResult,
		liveEvents,
		reconnectEvents,
		activeRunId,
		runError,
		canSelfHealRun,
		hasFreshServerRun,
		// The one primary action for the header
		primaryAction,
		canChat,
		runBlockedReason,
		// Heal PROPOSAL (no longer a second run result)
		healStatus: healRun.status,
		healProposal: healRun.proposal,
		healProposalPlanId: healRun.planId,
		healNotes: healRun.notes,
		healDeclineReason: healRun.declineReason,
		healStage: healRun.stage,
		healTriageCategory: healRun.triageCategory,
		healDeciding: healRun.deciding,
		hasHealProposal: healRun.hasProposal,
		healDisabled,
		acceptHeal: healRun.accept,
		rejectHeal: healRun.reject,
		runStartedAtMs,
		selectedResult,
		showResultMascot,
		canRun,
		selectedPlanPreview,
		// UI state
		liveExpanded,
		setLiveExpanded,
		showCredentialError,
		setShowCredentialError,
		// Derived
		planAnchors,
		clarificationEvent,
		completedRuns,
		invalidateRunsForPlan,
		runsForPlan,
		// Mutations
		updatePlanMutation,
		abortRunMutation,
		verifyPlan,
		getAuthUrlMutation,
		// Handlers — identity-stable (see `handlers` above)
		...handlers,
		// Destructive-run confirmation gate
		pendingDestructiveRun,
		// Optimistic stop state
		stopping,
	};
}
